// public/v3/src/pages/apcm-fallback.jsx — third top-level tab.
//
// Lists below-target CCM patients for the active month so the operator can
// decide whether to bill APCM instead of leaving them invisible at closeout.
// The primary queue stays focused on 2+ chronic CCM patients. Valid G0556
// candidates (0/1 chronic) remain a secondary, opt-in section because this
// practice does not usually bill them but wants a future path available.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ccmApi } from '../lib/api';
import { suggestApcmTier, hasFreshQmbConfirmation, hasMedicaid, hasMedicare } from '../lib/apcm-suggest';
import { isMedicaidInsurance, isMedicareInsurance } from '../lib/eligibility-infer';
import { payerProgramBlocker } from '../lib/payer-rules';
import { qmbG0558Comparison } from '../lib/qmb-optimizer';
import { chronicCount } from '../lib/ai-suggest';
import { ccmMonthlyMinutes, BILLING_TIME_TARGETS } from '../lib/push-emr';
import { isWorkflowPatient, isBhiEnrolledPatient } from '../lib/workflow';
import { bhiGatesForCode, resolveBhiBillCodeWithGates } from '../lib/bhi-billing';
import { QmbPill } from '../components/qmb-pill.jsx';
import { EligibilityPanel } from '../components/eligibility-panel.jsx';
import { auditApcmConsentChange } from '../lib/consent-audit';
import { confirmDialog } from '../components/confirm-dialog';

// APCM is a valid fallback only for standard/staff/complex CCM. The add-on
// codes stay eligible because they bill as continuations of the base CCM tier
// and can show up on legacy or manually adjusted patient records.
const APCM_FALLBACK_ELIGIBLE = new Set(['99490', '99491', '99487', '99439', '99437', '99489']);
const APCM_CODES = new Set(['G0556', 'G0557', 'G0558']);

function isTargetBearingProgram(program) {
  if (!program) return false;
  return (BILLING_TIME_TARGETS[program] || 0) > 0;
}
function canFallbackToApcm(program) {
  return APCM_FALLBACK_ELIGIBLE.has(program);
}
function isApcmFallbackCandidateProgram(program) {
  return canFallbackToApcm(program) && isTargetBearingProgram(program);
}

function getApcmAutoFallbackPolicy(raw) {
  const cfg = raw?.billing?.apcmAutoFallback;
  return {
    enabled: cfg?.enabled === true,
    allowG0556: cfg?.allowG0556 === true,
    useQmbOnlyWhenConfirmed: cfg?.useQmbOnlyWhenConfirmed !== false,
  };
}

function hasApcmConsent(raw, combinedConsent) {
  if (combinedConsent) return true;
  const c = raw?.apcmConsent;
  if (c === true) return true;
  if (typeof c === 'string') return c.toLowerCase() === 'signed' || c.toLowerCase() === 'true';
  return !!(c && (c.signed === true || c.signed === 'true' || c.status === 'signed'));
}

function apcmConsentBasis(raw, combinedConsent) {
  if (combinedConsent) return 'combined-ccm';
  if (hasApcmConsent(raw, false)) return 'patient-apcm-consent';
  return '';
}

function shouldCaptureApcmConsent(raw, combinedConsent) {
  return !combinedConsent && !hasApcmConsent(raw, false);
}

function buildApcmConsent(date) {
  return { signed: true, date, source: 'verbal' };
}

function hasConfirmedQmb(raw) {
  return hasFreshQmbConfirmation({ _raw: raw });
}

function getApcmMap(raw) {
  return raw?.apcmMonth && typeof raw.apcmMonth === 'object' ? raw.apcmMonth : {};
}

function isAlreadyBilled(patientId, month, billedState) {
  const entry = month && billedState && billedState[month] && billedState[month][patientId];
  if (!entry) return false;
  if (entry && typeof entry === 'object') {
    const keys = Object.keys(entry);
    if (keys.length === 1 && keys[0] === 'bhi_bn') return false;
  }
  return true;
}

function isApcmCodeAllowedForPatient(p, code) {
  if (!APCM_CODES.has(code)) return false;
  // APCM G-codes are Medicare-only — payer-blocked patients (medicaid,
  // commercial) can never accept an APCM decision, whatever their chronic
  // count. getDecisionBlockers surfaces the human-readable reason.
  if (payerProgramBlocker(p.raw?.payerClass, code)) return false;
  if (code === 'G0556') return p.chronic < 2;
  if (code === 'G0557') return p.chronic >= 2;
  if (code === 'G0558') return p.chronic >= 2 && hasConfirmedQmb(p.raw);
  return false;
}

function qmbSafeApcmSuggestion(p) {
  const sug = suggestApcmTier({ _raw: p.raw });
  if (sug.code !== 'G0558' || hasConfirmedQmb(p.raw)) return sug;
  return {
    ...sug,
    code: 'G0557',
    label: 'G0557 Level 2',
    reason: '2+ chronic; confirm QMB via Elation eligibility to use G0558',
  };
}

function fullName(raw) {
  return `${raw.fname || ''} ${raw.lname || ''}`.trim() || raw.name || raw.mrn || '';
}
function nameInitials(raw) {
  return fullName(raw).split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();
}
function avSlotFor(id) {
  const tail = String(id || '').slice(-2);
  const n = parseInt(tail, 16);
  if (!Number.isFinite(n)) return 1;
  return (n % 6) + 1;
}
function ageFromDob(dob) {
  if (!dob) return null;
  const t = Date.parse(dob);
  if (!Number.isFinite(t)) return null;
  const yrs = (Date.now() - t) / (365.2425 * 24 * 3600 * 1000);
  if (yrs < 0 || yrs > 130) return null;
  return Math.floor(yrs);
}
function programLabel(p) {
  if (p === '99490') return 'CCM 99490';
  if (p === '99491') return 'CCM 99491';
  if (p === '99487') return 'CCM 99487';
  if (p === '99439') return 'CCM add-on 99439';
  if (p === '99437') return 'CCM add-on 99437';
  if (p === '99489') return 'CCM add-on 99489';
  return p;
}

function bhiG0570StatusForRow(p, month, apcmContextActive = false) {
  if (!isBhiEnrolledPatient(p.raw)) return null;
  if (apcmContextActive) {
    const gates = bhiGatesForCode(p.raw, month, 'G0570');
    return { code: 'G0570', gatesOk: gates.ok, blockers: gates.blockers };
  }
  return resolveBhiBillCodeWithGates(p.raw, month);  // { code, gatesOk, blockers }
}

function BhiG0570Chip({ status, apcmContextActive }) {
  if (!status) return null;
  if (!apcmContextActive) {
    return <span className="apcm-row-g0570 muted">BHI enrolled</span>;
  }
  if (status.gatesOk) {
    return <span className="apcm-row-g0570 accent">+ G0570</span>;
  }
  return (
    <span className="apcm-row-g0570 warn" title={status.blockers[0] || ''}>
      + G0570 blocked
    </span>
  );
}

// Operational rows for APCM fallback = active + signed/onboarded +
// CCM fallback candidate. Enrollment and consent capture belong on Onboard;
// this page starts after time logging, once a patient can actually be decided.
function isOperationalRow(row) {
  const data = row && row.data && typeof row.data === 'object' ? row.data : row;
  return isWorkflowPatient(data)
    && data.consent === 'signed'
    && isApcmFallbackCandidateProgram(data.billing?.program);
}

// Adapter: minimum fields needed for row rendering + heuristic input.
function adapt(row) {
  if (!row || typeof row !== 'object') return null;
  const data = row.data && typeof row.data === 'object' ? row.data : row;
  return {
    id: row.id,
    raw: data,
    _raw: data, // for compat with components that read patient._raw
    name: fullName(data),
    mrn: data.mrn || '',
    age: ageFromDob(data.dob),
    sex: data.sex || '',
    program: (data.billing && data.billing.program) || '',
    consent: data.consent || 'pending',
    chronic: chronicCount({ _raw: data }),
    avSlot: avSlotFor(row.id),
    insurances: Array.isArray(data.insurances) ? data.insurances : [],
  };
}

export function ApcmFallbackPage({ month, focusPatient, onFocusConsumed, onBadgeChange, hideNoChronic, programFilter = 'ccm', bhiEnabled = false }) {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [handledOpen, setHandledOpen] = useState(false);
  const [showBelowChronic, setShowBelowChronic] = useState(!hideNoChronic);
  const [combinedConsent, setCombinedConsent] = useState(false);
  const [billedState, setBilledState] = useState({});
  const [billedStateUnavailable, setBilledStateUnavailable] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState({}); // patientId -> bool, true while Accept is in flight
  const [selected, setSelected] = useState(new Set());
  const [tierMenuOpen, setTierMenuOpen] = useState(false);
  const [autoApplying, setAutoApplying] = useState(false);
  const [eligibilityExpanded, setEligibilityExpanded] = useState({});
  const tierMenuRef = useRef(null);
  const rowRefs = useRef({});
  const autoAttemptedRef = useRef(new Set());

  async function loadDecisionContext() {
    const [rows, settings, billedResult] = await Promise.all([
      ccmApi.listPatients(),
      ccmApi.getSettings(),
      ccmApi.getBilledState()
        .then((billed) => ({ ok: true, billed: billed || {} }))
        .catch(() => ({ ok: false, billed: {} })),
    ]);
    const v = settings?.combined_care_mgmt_consent;
    return {
      patients: (rows || []).filter(isOperationalRow).map(adapt).filter(Boolean),
      combinedConsent: v === true || v === 'true',
      billedState: billedResult.billed,
      billedStateUnavailable: !billedResult.ok,
    };
  }

  function applyDecisionContext(ctx) {
    setPatients(ctx.patients);
    setCombinedConsent(ctx.combinedConsent);
    setBilledState(ctx.billedState);
    setBilledStateUnavailable(ctx.billedStateUnavailable);
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadDecisionContext()
      .then((ctx) => {
        if (cancelled) return;
        applyDecisionContext(ctx);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message || 'Failed to load patients');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [month]);

  // Refresh one patient's local data after a pill writes back.
  function patchLocalPatient(id, nextRaw) {
    setPatients(prev => prev.map(p => p.id === id ? { ...p, raw: nextRaw, _raw: nextRaw,
      program: (nextRaw.billing && nextRaw.billing.program) || '',
      consent: nextRaw.consent || 'pending',
      chronic: chronicCount({ _raw: nextRaw }),
      insurances: Array.isArray(nextRaw.insurances) ? nextRaw.insurances : [] } : p));
  }

  function patchFromEligibility(id, payload) {
    const nextRaw = payload?.patient?.data;
    if (nextRaw && typeof nextRaw === 'object') {
      patchLocalPatient(id, nextRaw);
    } else {
      loadDecisionContext().then(applyDecisionContext).catch(() => {});
    }
  }

  function targetFor(p) {
    return BILLING_TIME_TARGETS[p.program] != null ? BILLING_TIME_TARGETS[p.program] : 20;
  }

  // CCM-family basis, matching push eligibility (push-emr.js) — an all-family
  // count lets BHI minutes satisfy the CCM target here, hiding dual CCM+BHI
  // patients from this queue while their CCM push still fails short.
  function isBelowTarget(p) {
    return ccmMonthlyMinutes(p.raw, month) < targetFor(p);
  }

  // QMB optimizer (finding 8): for a confirmed-QMB patient, coinsurance on
  // every code is uncollectible (NY lesser-of), so a met-target month with
  // few logged minutes still nets less as CCM than G0558's flat case rate.
  // Such months belong in this queue even though the CCM threshold is met.
  function qmbOptimizerApplies(p) {
    if (p.chronic < 2) return false; // G0558 requires 2+ chronic
    if (!hasConfirmedQmb(p.raw)) return false;
    return qmbG0558Comparison(ccmMonthlyMinutes(p.raw, month)).g0558Wins;
  }

  // Queue admission: the classic below-target case, plus QMB months where
  // flipping to G0558 nets more despite the met threshold.
  function qualifiesForApcmQueue(p) {
    return isBelowTarget(p) || qmbOptimizerApplies(p);
  }

  function isSkippedForMonth(p, decisionMonth = month) {
    const skipMap = p.raw.skipMonth && typeof p.raw.skipMonth === 'object' ? p.raw.skipMonth : {};
    return !!(decisionMonth && skipMap[decisionMonth]);
  }

  function hasDecisionForMonth(p, decisionMonth = month) {
    return !!(decisionMonth && getApcmMap(p.raw)[decisionMonth]);
  }

  function passesProgramLens(p) {
    if (!bhiEnabled || programFilter !== 'bhi') return true;
    return isBhiEnrolledPatient(p.raw);
  }

  function getDecisionBlockers(p, sug, ctx = {}) {
    const blockers = [];
    const code = sug?.code || '';
    const decisionMonth = ctx.month || month;
    const ctxCombinedConsent = ctx.combinedConsent ?? combinedConsent;
    const ctxBilledState = ctx.billedState || billedState;
    const ctxBilledStateUnavailable = ctx.billedStateUnavailable ?? billedStateUnavailable;
    if (!decisionMonth) blockers.push('missing month');
    if (ctxBilledStateUnavailable) blockers.push('billed state unavailable');
    if (isAlreadyBilled(p.id, decisionMonth, ctxBilledState)) blockers.push('already submitted');
    if (!isApcmFallbackCandidateProgram(p.program)) blockers.push('not a CCM fallback program');
    if (p.consent !== 'signed') blockers.push('consent not signed');
    if (isSkippedForMonth(p, decisionMonth)) blockers.push('skipped this month');
    // Met threshold blocks the decision UNLESS this is the QMB-optimizer case
    // AND the code being accepted is G0558 itself — flipping a met CCM month
    // to a lower-paying G0556/G0557 never makes sense.
    if (!isBelowTarget(p) && !(code === 'G0558' && qmbOptimizerApplies(p))) {
      blockers.push('CCM threshold already met');
    }
    if (!ctx.captureConsent && !hasApcmConsent(p.raw, ctxCombinedConsent)) blockers.push('APCM consent missing');
    if (!isApcmCodeAllowedForPatient(p, code)) blockers.push('APCM code not allowed for patient');
    return blockers;
  }

  function autoFallbackCandidate(p) {
    const policy = getApcmAutoFallbackPolicy(p.raw);
    if (!policy.enabled) return null;
    const sug = qmbSafeApcmSuggestion(p);
    if (sug.code === 'G0556' && !policy.allowG0556) return null;
    const blockers = getDecisionBlockers(p, sug);
    if (blockers.length) return null;
    return {
      ...sug,
      reason: sug.code === 'G0558'
        ? 'Auto fallback: below CCM threshold; QMB confirmed'
        : sug.code === 'G0556'
          ? 'Auto fallback: explicit G0556 opt-in, below CCM threshold'
          : 'Auto fallback: below CCM threshold',
    };
  }

  // Shared predicate: passes the time-target + decided/submitted gate for the
  // primary action queue.
  function passesBaseFilter(p, q, ctx = {}) {
    const decisionMonth = ctx.month || month;
    const ctxBilledState = ctx.billedState || billedState;
    const decided = hasDecisionForMonth(p, decisionMonth);
    if (!isApcmFallbackCandidateProgram(p.program)) return false;
    if (!passesProgramLens(p)) return false;
    if (p.consent !== 'signed') return false;
    if (isSkippedForMonth(p, decisionMonth)) return false;
    if (!qualifiesForApcmQueue(p)) return false;
    if (isAlreadyBilled(p.id, decisionMonth, ctxBilledState) && !decided) return false;
    if (decided) return false;
    if (q) {
      const hay = `${p.name} ${p.mrn}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  }

  function passesHandledFilter(p, q, ctx = {}) {
    const decisionMonth = ctx.month || month;
    const ctxBilledState = ctx.billedState || billedState;
    const decided = hasDecisionForMonth(p, decisionMonth);
    const submitted = isAlreadyBilled(p.id, decisionMonth, ctxBilledState);
    if (!isApcmFallbackCandidateProgram(p.program)) return false;
    if (!passesProgramLens(p)) return false;
    if (p.consent !== 'signed') return false;
    if (isSkippedForMonth(p, decisionMonth)) return false;
    if (!qualifiesForApcmQueue(p)) return false;
    if (!decided && !submitted) return false;
    if (q) {
      const hay = `${p.name} ${p.mrn}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  }

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return patients.filter(p => p.chronic >= 2 && passesBaseFilter(p, q));
  }, [patients, query, month, billedState, programFilter, bhiEnabled]);

  const handledVisible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return patients.filter(p => p.chronic >= 2 && passesHandledFilter(p, q));
  }, [patients, query, month, billedState, programFilter, bhiEnabled]);

  const belowChronic = useMemo(() => {
    const q = query.trim().toLowerCase();
    return patients.filter(p => p.chronic < 2 && passesBaseFilter(p, q));
  }, [patients, query, month, billedState, programFilter, bhiEnabled]);

  // Deep-link from Push modal's `Set APCM tier →` (wired in Phase 9).
  useEffect(() => {
    if (!focusPatient?.id) return;
    const el = rowRefs.current[focusPatient.id];
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('flash');
      setTimeout(() => el.classList.remove('flash'), 1200);
    }
    onFocusConsumed?.();
  }, [focusPatient?.id, focusPatient?.ts]);

  // The badge count = primary action queue rows: not decided, not submitted,
  // and scoped to the active chrome lens.
  const undecidedCount = useMemo(() => {
    return patients.filter(p => {
      if (p.chronic < 2) return false;
      if (!isApcmFallbackCandidateProgram(p.program)) return false;
      if (!passesProgramLens(p)) return false;
      if (p.consent !== 'signed') return false;
      if (isSkippedForMonth(p)) return false;
      if (!qualifiesForApcmQueue(p)) return false;
      if (hasDecisionForMonth(p)) return false;
      if (isAlreadyBilled(p.id, month, billedState)) return false;
      return true;
    }).length;
  }, [patients, month, billedState, programFilter, bhiEnabled]);

  useEffect(() => { onBadgeChange?.(undecidedCount); }, [undecidedCount]);

  useEffect(() => {
    if (!tierMenuOpen) return;
    const h = (e) => {
      if (tierMenuRef.current && !tierMenuRef.current.contains(e.target)) {
        setTierMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [tierMenuOpen]);

  useEffect(() => { setSelected(new Set()); }, [month]);

  function flashToast(msg, opts = {}) {
    setToast({ text: msg, error: !!opts.error });
    setTimeout(() => setToast(null), 2800);
  }

  const bulkSelectable = useMemo(() => {
    return visible.filter((p) => {
      const sug = qmbSafeApcmSuggestion(p);
      return !busy[p.id] && getDecisionBlockers(p, sug, { captureConsent: true }).length === 0;
    });
  }, [visible, busy, month, combinedConsent, billedState, billedStateUnavailable]);
  const allSelected = bulkSelectable.length > 0 && bulkSelectable.every(p => selected.has(p.id));
  const selPatients = bulkSelectable.filter(p => selected.has(p.id));
  const selectedCount = selPatients.length;

  function toggleRow(id) {
    if (!bulkSelectable.some(p => p.id === id)) return;
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  }

  // Styled confirm (drop-in for the old native confirm — same copy, but the
  // consent attestation renders as a proper line instead of a truncated blob).
  function confirmApcmDecision(count, label, consentCount) {
    const patientLabel = count === 1 ? 'patient' : 'patients';
    const consentLine = consentCount > 0
      ? `Attests verbal APCM consent obtained for ${consentCount} ${consentCount === 1 ? 'patient' : 'patients'}. Audit-logged.`
      : 'APCM consent is already on file or inherited.';
    return confirmDialog({
      title: `Enroll ${count} ${patientLabel} in APCM ${label} for ${month}?`,
      bullets: [consentLine],
      confirmLabel: 'Enroll',
    });
  }

  function renderApcmConsentStatus(p) {
    if (combinedConsent) {
      return (
        <span className="apcm-pill ok" title="Combined CCM/APCM consent policy: CCM consent on file covers APCM">
          <span className="apcm-pill-btn">APCM consent ✓ inherited</span>
        </span>
      );
    }
    if (!hasApcmConsent(p.raw, false)) return null;
    const c = p.raw.apcmConsent || {};
    const date = typeof c === 'object' ? c.date : '';
    const source = typeof c === 'object' ? c.source : 'verbal';
    return (
      <span className="apcm-pill ok" title={`APCM consent ${date || 'date not set'} (${source})`}>
        <span className="apcm-pill-btn">APCM consent ✓ <span className="tabnum">{date || 'date not set'}</span></span>
      </span>
    );
  }
  function toggleAll() {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(bulkSelectable.map(p => p.id)));
  }

  function renderInsuranceBadges(ins) {
    const medicare = ins.find(isMedicareInsurance);
    const medicaid = ins.find(isMedicaidInsurance);
    if (!medicare && !medicaid) {
      return <span className="badge warn">No insurance on file</span>;
    }
    return (
      <>
        {medicare && <span className="apcm-badge medicare">Medicare</span>}
        {medicaid && <span className="apcm-badge medicaid">Medicaid</span>}
      </>
    );
  }

  // Write data.apcmMonth[month] with the audit snapshot. consentBasis records
  // which path satisfied APCM consent at decision time so audit can reconstruct
  // the basis even if patient-level fields change later.
  //
  // Body includes _updatedAt so the server's 409 optimistic-lock fires on stale
  // writes. On success we capture the response's updatedAt back into local _raw
  // so a follow-up edit doesn't 409 against the freshly-bumped row.
  async function saveApcmDecision(p, sug, opts = {}) {
    if (!month) return;
    const decisionCtx = {
      ...(opts.ctx || {}),
      captureConsent: opts.captureConsent === true || opts.ctx?.captureConsent === true,
    };
    const blockers = getDecisionBlockers(p, sug, decisionCtx);
    if (blockers.length) {
      if (!opts.quiet) flashToast(`${p.name}: ${blockers[0]}`, { error: true });
      return;
    }
    setBusy(b => ({ ...b, [p.id]: true }));
    try {
      const dualEligible = hasMedicaid({ _raw: p.raw }) && hasMedicare({ _raw: p.raw });
      const qmbConfirmed = !!(p.raw.qmb && p.raw.qmb.confirmed);
      const ctxCombinedConsent = decisionCtx.combinedConsent ?? combinedConsent;
      const captureConsent = decisionCtx.captureConsent === true && shouldCaptureApcmConsent(p.raw, ctxCombinedConsent);
      // New consents default to the patient's first Elation office visit
      // (matching CCM/BHI enrollment defaults), falling back to today. Bulk
      // callers prefetch a firstVisitDates map so we don't issue one lookup
      // per row; the per-row path fetches its own. Never touches an existing
      // consent — captureConsent is false when one is already on file.
      const today = new Date().toISOString().slice(0, 10);
      let consentDate = today;
      let dateSource = 'today';
      const elid = p.raw.elation_id ? String(p.raw.elation_id) : null;
      if (captureConsent && elid) {
        let fv = null;
        if (opts.firstVisitDates) {
          fv = opts.firstVisitDates[elid] || null;
        } else {
          try {
            const res = await ccmApi.firstVisitDates([elid]);
            fv = (res.results && res.results[elid]) || null;
          } catch (_) {}
        }
        if (fv) { consentDate = fv; dateSource = 'first visit'; }
      }
      const nextConsent = captureConsent ? buildApcmConsent(consentDate) : null;
      const consentBasis = ctxCombinedConsent || hasApcmConsent(p.raw, false) || captureConsent
        ? apcmConsentBasis(nextConsent ? { ...p.raw, apcmConsent: nextConsent } : p.raw, ctxCombinedConsent)
        : '';
      const prevApcm = getApcmMap(p.raw);
      const nextApcm = {
        ...prevApcm,
        [month]: {
          code: sug.code,
          reason: sug.reason,
          dualEligible,
          qmbConfirmed,
          consentBasis,
          source: opts.source || 'manual',
          setBy: '',          // server fills via audit
          ts: new Date().toISOString(),
        },
      };
      // Preserve _updatedAt on the body so the optimistic-lock check fires.
      const next = nextConsent
        ? { ...p.raw, apcmConsent: nextConsent, apcmMonth: nextApcm }
        : { ...p.raw, apcmMonth: nextApcm };
      const resp = await ccmApi.updatePatient(p.id, next);
      if (nextConsent) {
        await auditApcmConsentChange(p, p.raw.apcmConsent || null, nextConsent, {
          mode: 'APCM enrollment verbal',
          date: consentDate,
          dateSource,
          source: 'verbal',
        });
      }
      const fresh = resp && resp.updatedAt ? resp.updatedAt : null;
      patchLocalPatient(p.id, fresh ? { ...next, _updatedAt: fresh } : next);
      if (!opts.quiet) flashToast(`${p.name} → ${sug.code} for ${month}`);
      return true;
    } catch (e) {
      if (e && e.status === 409) {
        if (!opts.quiet) flashToast(`${p.name} was updated elsewhere. Reloaded latest data; try again.`, { error: true });
        try {
          const rows = await ccmApi.listPatients();
          setPatients((rows || []).filter(isOperationalRow).map(adapt).filter(Boolean));
        } catch (_) {}
      } else {
        if (!opts.quiet) flashToast(`Accept failed: ${e?.message || 'unknown'}`, { error: true });
      }
      return false;
    } finally {
      setBusy(b => { const n = { ...b }; delete n[p.id]; return n; });
    }
  }

  async function acceptRow(p, sug) {
    const blockers = getDecisionBlockers(p, sug, { captureConsent: true });
    if (blockers.length) {
      flashToast(`${p.name}: ${blockers[0]}`, { error: true });
      return;
    }
    const consentCount = shouldCaptureApcmConsent(p.raw, combinedConsent) ? 1 : 0;
    if (!(await confirmApcmDecision(1, sug.code, consentCount))) return;
    return saveApcmDecision(p, sug, { source: 'manual', captureConsent: true });
  }

  function bulkEligibleRows(rows, sugForRow, ctx = {}) {
    const eligible = [];
    const skipped = {
      consent: 0,
      state: 0,
      billed: 0,
      code: 0,
      other: 0,
    };
    for (const p of rows) {
      const sug = sugForRow(p);
      const blockers = getDecisionBlockers(p, sug, ctx);
      if (!blockers.length) {
        eligible.push({ p, sug });
        continue;
      }
      const joined = blockers.join(' · ');
      if (joined.includes('consent')) skipped.consent++;
      else if (joined.includes('billed state')) skipped.state++;
      else if (joined.includes('submitted')) skipped.billed++;
      else if (joined.includes('code')) skipped.code++;
      else skipped.other++;
    }
    return { eligible, skipped };
  }

  // Batch first-visit lookup for the bulk paths — mirrors onboard.jsx's
  // bulkEnroll. Returns {} on error / no elation ids so saveApcmDecision
  // falls back to today per row.
  async function fetchFirstVisitDates(rows) {
    const elationIds = rows
      .map(p => p.raw.elation_id ? String(p.raw.elation_id) : null)
      .filter(Boolean);
    if (!elationIds.length) return {};
    try { return (await ccmApi.firstVisitDates(elationIds)).results || {}; }
    catch (_) { return {}; }
  }

  function skippedTail(skipped) {
    const tail = [];
    if (skipped.consent) tail.push(`${skipped.consent} skipped (APCM consent missing)`);
    if (skipped.state) tail.push(`${skipped.state} skipped (billed state unavailable)`);
    if (skipped.billed) tail.push(`${skipped.billed} skipped (already submitted)`);
    if (skipped.code) tail.push(`${skipped.code} skipped (tier not allowed)`);
    if (skipped.other) tail.push(`${skipped.other} skipped`);
    return tail;
  }

  async function bulkAcceptSuggestions() {
    if (selPatients.length === 0) return;
    let rows = selPatients;
    let ctx = { captureConsent: true };
    try {
      const fresh = await loadDecisionContext();
      applyDecisionContext(fresh);
      ctx = {
        combinedConsent: fresh.combinedConsent,
        billedState: fresh.billedState,
        billedStateUnavailable: fresh.billedStateUnavailable,
        captureConsent: true,
      };
      const q = query.trim().toLowerCase();
      rows = fresh.patients.filter(p => selected.has(p.id) && p.chronic >= 2 && passesBaseFilter(p, q, { ...ctx, month }));
    } catch (_) {
      // Fall back to the current in-memory rows; saveApcmDecision still performs
      // the same blocker check and optimistic-lock write per patient.
    }
    const { eligible, skipped } = bulkEligibleRows(rows, qmbSafeApcmSuggestion, ctx);
    if (eligible.length === 0) {
      const tail = skippedTail(skipped);
      flashToast(`No accept-eligible rows${tail.length ? ` — ${tail.join(' · ')}` : ''}`, { error: true });
      return;
    }
    const consentCount = eligible.filter(({ p }) => shouldCaptureApcmConsent(p.raw, ctx.combinedConsent ?? combinedConsent)).length;
    if (!(await confirmApcmDecision(eligible.length, 'suggestions', consentCount))) return;
    try {
      const firstVisitDates = await fetchFirstVisitDates(eligible.map(({ p }) => p));
      let accepted = 0;
      for (const { p, sug } of eligible) {
        if (await saveApcmDecision(p, sug, { source: 'manual', quiet: true, ctx, captureConsent: true, firstVisitDates })) accepted++;
      }
      setSelected(new Set());
      const tail = skippedTail(skipped);
      flashToast(tail.length
        ? `Accepted ${accepted} · ${tail.join(' · ')}`
        : `Accepted ${accepted}`);
    } catch (e) {}
  }

  async function bulkSetTier(code) {
    if (selPatients.length === 0) return;
    let rows = selPatients;
    let ctx = { captureConsent: true };
    try {
      const fresh = await loadDecisionContext();
      applyDecisionContext(fresh);
      ctx = {
        combinedConsent: fresh.combinedConsent,
        billedState: fresh.billedState,
        billedStateUnavailable: fresh.billedStateUnavailable,
        captureConsent: true,
      };
      const q = query.trim().toLowerCase();
      rows = fresh.patients.filter(p => selected.has(p.id) && p.chronic >= 2 && passesBaseFilter(p, q, { ...ctx, month }));
    } catch (_) {}
    const { eligible, skipped } = bulkEligibleRows(rows, (p) => {
      const reason = code === 'G0558'
        ? 'Operator override → Level 3'
        : code === 'G0557'
          ? 'Operator override → Level 2'
          : 'Operator override → Level 1';
      return { code, label: code, reason };
    }, ctx);
    if (eligible.length === 0) {
      const tail = skippedTail(skipped);
      flashToast(`No accept-eligible rows${tail.length ? ` — ${tail.join(' · ')}` : ''}`, { error: true });
      return;
    }
    const consentCount = eligible.filter(({ p }) => shouldCaptureApcmConsent(p.raw, ctx.combinedConsent ?? combinedConsent)).length;
    if (!(await confirmApcmDecision(eligible.length, code, consentCount))) return;
    const firstVisitDates = await fetchFirstVisitDates(eligible.map(({ p }) => p));
    let accepted = 0;
    for (const { p, sug } of eligible) {
      if (await saveApcmDecision(p, sug, { source: 'manual', quiet: true, ctx, captureConsent: true, firstVisitDates })) accepted++;
    }
    setSelected(new Set());
    const tail = skippedTail(skipped);
    flashToast(tail.length
      ? `Set ${accepted} → ${code} · ${tail.join(' · ')}`
      : `Set ${accepted} → ${code}`);
  }

  async function toggleAutoFallback(p, enabled) {
    if (enabled && month) {
      // The toggle is a future-month preference; use Accept for the current month.
      autoAttemptedRef.current.add(`${p.id}:${month}`);
    }
    const priorBilling = p.raw.billing && typeof p.raw.billing === 'object' ? p.raw.billing : {};
    const priorPolicy = getApcmAutoFallbackPolicy(p.raw);
    const nextPolicy = enabled
      ? {
          enabled: true,
          allowG0556: p.chronic < 2,
          useQmbOnlyWhenConfirmed: true,
        }
      : {
          ...priorPolicy,
          enabled: false,
        };
    const next = {
      ...p.raw,
      billing: {
        ...priorBilling,
        apcmAutoFallback: nextPolicy,
      },
    };
    setBusy(b => ({ ...b, [p.id]: true }));
    try {
      const resp = await ccmApi.updatePatient(p.id, next);
      const fresh = resp && resp.updatedAt ? resp.updatedAt : null;
      patchLocalPatient(p.id, fresh ? { ...next, _updatedAt: fresh } : next);
      flashToast(enabled
        ? `${p.name}: auto fallback on${p.chronic < 2 ? ' for G0556' : ''}`
        : `${p.name}: auto fallback off`);
    } catch (e) {
      if (e && e.status === 409) {
        flashToast(`${p.name} was updated elsewhere. Reloaded latest data; try again.`, { error: true });
        try {
          const rows = await ccmApi.listPatients();
          setPatients((rows || []).filter(isOperationalRow).map(adapt).filter(Boolean));
        } catch (_) {}
      } else {
        flashToast(`Auto fallback update failed: ${e?.message || 'unknown'}`, { error: true });
      }
    } finally {
      setBusy(b => { const n = { ...b }; delete n[p.id]; return n; });
    }
  }

  useEffect(() => {
    if (loading || autoApplying || !month || billedStateUnavailable) return;
    const candidates = [];
    for (const p of patients) {
      const key = `${p.id}:${month}`;
      if (autoAttemptedRef.current.has(key)) continue;
      if (hasDecisionForMonth(p)) continue;
      const sug = autoFallbackCandidate(p);
      if (sug) candidates.push({ p, sug, key });
    }
    if (!candidates.length) return;

    let cancelled = false;
    setAutoApplying(true);
    (async () => {
      let applied = 0;
      for (const { p, sug, key } of candidates) {
        if (cancelled) break;
        autoAttemptedRef.current.add(key);
        if (await saveApcmDecision(p, sug, { source: 'auto', quiet: true })) applied++;
      }
      if (!cancelled && applied > 0) {
        flashToast(`Auto fallback applied to ${applied} patient${applied === 1 ? '' : 's'} for ${month}`);
      }
      setAutoApplying(false);
    })();

    return () => { cancelled = true; };
  }, [loading, autoApplying, patients, month, combinedConsent, billedState, billedStateUnavailable]);

  async function reverseRow(p) {
    if (!month) return;
    if (billedStateUnavailable) {
      flashToast('Billed state could not be verified. Reload before changing APCM fallback decisions.', { error: true });
      return;
    }
    if (isAlreadyBilled(p.id, month, billedState)) {
      flashToast(`${p.name} is already submitted for ${month}. Reverse billing state before changing APCM fallback.`, { error: true });
      return;
    }
    if (!(await confirmDialog({
      title: `Reverse APCM fallback for ${p.name}?`,
      body: 'The patient will return to the undecided pool.',
      confirmLabel: 'Reverse',
      danger: true,
    }))) return;
    setBusy(b => ({ ...b, [p.id]: true }));
    try {
      // Preserve _updatedAt on the body for the optimistic-lock check.
      const apcmMap = (p.raw.apcmMonth && typeof p.raw.apcmMonth === 'object') ? { ...p.raw.apcmMonth } : {};
      delete apcmMap[month];
      const next = { ...p.raw };
      if (Object.keys(apcmMap).length) next.apcmMonth = apcmMap;
      else delete next.apcmMonth;
      const resp = await ccmApi.updatePatient(p.id, next);
      const fresh = resp && resp.updatedAt ? resp.updatedAt : null;
      patchLocalPatient(p.id, fresh ? { ...next, _updatedAt: fresh } : next);
      flashToast(`Reversed — ${p.name} returned to undecided pool`);
    } catch (e) {
      if (e && e.status === 409) {
        flashToast(`${p.name} was updated elsewhere. Reloaded latest data; try again.`, { error: true });
        try {
          const rows = await ccmApi.listPatients();
          setPatients((rows || []).filter(isOperationalRow).map(adapt).filter(Boolean));
        } catch (_) {}
      } else {
        flashToast(`Reverse failed: ${e?.message || 'unknown'}`, { error: true });
      }
    } finally {
      setBusy(b => { const n = { ...b }; delete n[p.id]; return n; });
    }
  }

  return (
    <section className="apcm-page">
      <header className="apcm-head">
        <div>
          <h2>APCM Fallback</h2>
          <div className="apcm-sub">
            Below-threshold CCM patients for {month}. Decide APCM here; Studio generates care plans, billing notes, and Elation pushes after the decision.
          </div>
          {billedStateUnavailable && (
            <div className="apcm-sub apcm-warning">
              Billed state could not be verified. APCM fallback writes are disabled until this reloads cleanly.
            </div>
          )}
        </div>
      </header>

      <div className="apcm-toolbar">
        <input
          type="search"
          className="apcm-search"
          placeholder="Search name or MRN…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <label className="apcm-toggle">
          <input type="checkbox" checked={showBelowChronic} onChange={(e) => setShowBelowChronic(e.target.checked)} />
          Show &lt;2 chronic
        </label>
        <span style={{ flex: 1 }} />
        {autoApplying && <span className="apcm-count">Auto-applying…</span>}
        <span className="apcm-count tabnum">{undecidedCount} need decision</span>
      </div>

      <div className="mobile-workflow-summary apcm-mobile-summary" aria-label="APCM queue summary">
        <div><span>Need decision</span><b className="tabnum">{undecidedCount}</b></div>
        <div><span>Selected</span><b className="tabnum">{selectedCount}</b></div>
        <div><span>Handled</span><b className="tabnum">{handledVisible.length}</b></div>
      </div>

      <div className="apcm-list-head">
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ink-3)' }}>
          <input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={bulkSelectable.length === 0} />
          Select eligible visible
        </label>
        <span className="apcm-count tabnum">{visible.length} need decision</span>
      </div>

      {selectedCount > 0 && (
        <div className="bulk-bar apcm-bulk">
          <span><b>{selectedCount}</b> selected</span>
          <span style={{ flex: 1 }} />
          <button className="btn sm" onClick={bulkAcceptSuggestions}>Accept suggestions</button>
          <div className="split-btn" ref={tierMenuRef}>
            <button className="btn sm" onClick={() => { setTierMenuOpen(false); bulkSetTier('G0557'); }}>
              Set {selectedCount} to G0557
            </button>
            <button
              className="btn sm split-btn-caret"
              aria-haspopup="menu"
              aria-expanded={tierMenuOpen}
              aria-label="Pick a tier"
              onClick={() => setTierMenuOpen(o => !o)}>▾</button>
            {tierMenuOpen && (
              <div className="split-btn-menu" role="menu">
                <button role="menuitem" onClick={() => { setTierMenuOpen(false); bulkSetTier('G0558'); }}>
                  G0558 — Level 3 (QMB)
                </button>
              </div>
            )}
          </div>
          <button className="btn ghost sm" onClick={() => setSelected(new Set())}>Clear</button>
        </div>
      )}

      <div className="apcm-list">
        {loading && <div className="apcm-empty">Loading…</div>}
        {!loading && error && <div className="apcm-empty" style={{ color: 'var(--bad)' }}>{error}</div>}
        {!loading && !error && visible.length === 0 && (
          <div className="apcm-empty">
            {patients.length === 0
              ? 'No patients in this practice. Run Elation Sync from the More drawer.'
              : query
                ? `No patients match "${query}".`
                : 'No below-target patients this month — nothing to decide.'}
          </div>
        )}
        {!loading && visible.map(p => {
          const target = targetFor(p);
          const mins = ccmMonthlyMinutes(p.raw, month);
          const shortBy = Math.max(0, target - mins);
          const sug = qmbSafeApcmSuggestion(p);
          const apcmMap = getApcmMap(p.raw);
          const decided = month ? apcmMap[month] : null;
          const autoPolicy = getApcmAutoFallbackPolicy(p.raw);
          const blockers = getDecisionBlockers(p, sug, { captureConsent: true });
          const acceptDisabled = blockers.length > 0 || busy[p.id];
          const acceptTitle = blockers[0] || `Accept ${sug.code} for ${month}`;
          const canBulkSelect = !acceptDisabled;
          return (
            <React.Fragment key={p.id}>
            <div
              ref={(el) => { rowRefs.current[p.id] = el; }}
              className={`roster-item apcm-row ${decided ? 'apcm-decided' : ''}`}
            >
              <input
                type="checkbox"
                checked={canBulkSelect && selected.has(p.id)}
                disabled={!canBulkSelect}
                title={canBulkSelect ? 'Select for bulk actions' : acceptTitle}
                onChange={() => toggleRow(p.id)}
                onClick={(e) => e.stopPropagation()}
                style={{ marginTop: 6 }}
              />
              <div className={`avatar av-${p.avSlot}`}>{nameInitials(p.raw)}</div>
              <div className="apcm-row-main">
                <div className="ri-name">
                  {p.name}
                  {p.age != null && <span className="apcm-age"> · {p.age}{p.sex ? ` ${p.sex[0].toUpperCase()}` : ''}</span>}
                  {p.mrn && <span className="apcm-mrn"> · MRN {p.mrn}</span>}
                </div>
                <div className="ri-meta apcm-meta">
                  <span className="badge">{p.chronic} chronic dx</span>
                  <span className="badge">{programLabel(p.program)}</span>
                  <span className="badge">{mins}/{target} min · {shortBy}m short</span>
                  {autoPolicy.enabled && (
                    <span className="badge apcm-auto-badge" title="Future short months can be decided automatically when all billing safeguards pass">
                      Auto fallback on
                    </span>
                  )}
                  {renderInsuranceBadges(p.insurances)}
                </div>
                <div className="ri-meta apcm-suggest">
                  Suggested: <b>{sug.label}</b> — <span style={{ color: 'var(--ink-2)' }}>{sug.reason}</span>
                </div>
                {!isBelowTarget(p) && qmbOptimizerApplies(p) && (() => {
                  const cmp = qmbG0558Comparison(mins);
                  return (
                    <div className="ri-meta" style={{ color: 'var(--accent-ink, var(--ink-2))' }}>
                      QMB month met at {mins}m, but G0558 nets ${cmp.apcmNet.toFixed(2)} vs CCM ${cmp.ccmNet.toFixed(2)} — coinsurance uncollectible either way (NY lesser-of).
                    </div>
                  );
                })()}
                <div className="apcm-pills">
                  <QmbPill patient={p} onSaved={(next) => patchLocalPatient(p.id, next)} />
                  {renderApcmConsentStatus(p)}
                  <label
                    className={`apcm-auto-toggle ${autoPolicy.enabled ? 'on' : ''}`}
                    title="When this patient misses the CCM threshold in a future month, automatically record the APCM fallback decision once consent and billing safeguards pass"
                  >
                    <input
                      type="checkbox"
                      checked={autoPolicy.enabled}
                      disabled={busy[p.id]}
                      onChange={(e) => toggleAutoFallback(p, e.target.checked)}
                    />
                    <span>Auto future fallback</span>
                  </label>
                  <BhiG0570Chip
                    status={bhiG0570StatusForRow(p, month, true)}
                    apcmContextActive={true}
                  />
                  <button
                    className="btn ghost sm"
                    onClick={() => setEligibilityExpanded(prev => ({ ...prev, [p.id]: !prev[p.id] }))}>
                    {eligibilityExpanded[p.id] ? 'Hide eligibility' : 'Eligibility'}
                  </button>
                </div>
                {decided && (
                  <div className="apcm-decided-stamp">
                    Decided · <b>{decided.code}</b>{decided.source === 'auto' ? ' · Auto' : ''} · {decided.ts?.slice(0, 10) || ''}
                  </div>
                )}
              </div>
              <div className="apcm-row-actions">
                {decided ? (
                  <>
                    <button
                      className="btn ghost sm"
                      disabled={busy[p.id]}
                      title="Reverse APCM fallback before choosing another tier"
                      onClick={() => reverseRow(p)}>
                      Reverse
                    </button>
                  </>
                ) : (
                  <button
                    className="btn primary sm"
                    disabled={acceptDisabled}
                    title={acceptTitle}
                    onClick={() => acceptRow(p, sug)}>
                    {busy[p.id] ? '…' : `Accept · ${sug.code}`}
                  </button>
                )}
              </div>
            </div>
            {eligibilityExpanded[p.id] ? (
              <div className="row-eligibility-drawer">
                <EligibilityPanel patient={p} onRefreshed={(payload) => patchFromEligibility(p.id, payload)} />
              </div>
            ) : null}
            </React.Fragment>
          );
        })}
      </div>

      <section className={`onboard-enrolled-section apcm-handled-section ${handledOpen ? 'is-open' : 'is-collapsed'}`} aria-label="Handled APCM fallback patients">
        <button
          type="button"
          className="onboard-enrolled-toggle apcm-handled-toggle"
          aria-expanded={handledOpen}
          onClick={() => setHandledOpen(o => !o)}
        >
          <span className="onboard-accordion-left">
            <span className={`onboard-accordion-chevron ${handledOpen ? 'open' : ''}`} aria-hidden="true">▾</span>
            <span className="onboard-accordion-main">
              <span className="onboard-accordion-title">Handled this month</span>
              <span className="onboard-accordion-sub">Submitted rows and APCM decisions that can be reviewed or reversed when safe</span>
            </span>
          </span>
          <span className="onboard-enrolled-meta">
            <span className="onboard-enrolled-count tabnum">{handledVisible.length} handled</span>
          </span>
        </button>

        {handledOpen && (
          <div className="onboard-enrolled-body apcm-handled-body">
            <div className="onboard-list-head onboard-enrolled-head">
              <span className="onboard-count tabnum">{handledVisible.length} handled</span>
            </div>
            <div className="apcm-list apcm-handled-list">
              {!loading && !error && handledVisible.length === 0 && (
                <div className="apcm-empty apcm-handled-empty">
                  {query ? `No handled patients match "${query}".` : 'No handled APCM rows this month.'}
                </div>
              )}
              {!loading && handledVisible.map(p => {
                const target = targetFor(p);
                const mins = ccmMonthlyMinutes(p.raw, month);
                const shortBy = Math.max(0, target - mins);
                const sug = qmbSafeApcmSuggestion(p);
                const apcmMap = getApcmMap(p.raw);
                const decided = month ? apcmMap[month] : null;
                const submitted = isAlreadyBilled(p.id, month, billedState);
                const submittedOnly = submitted && !decided;
                const apcmContextActive = !!decided;
                const autoPolicy = getApcmAutoFallbackPolicy(p.raw);
                return (
                  <React.Fragment key={p.id}>
                  <div
                    ref={(el) => { rowRefs.current[p.id] = el; }}
                    className={`roster-item apcm-row apcm-handled-row ${decided ? 'apcm-decided' : ''}`}
                  >
                    <div className={`avatar av-${p.avSlot}`}>{nameInitials(p.raw)}</div>
                    <div className="apcm-row-main">
                      <div className="ri-name">
                        {p.name}
                        {p.age != null && <span className="apcm-age"> · {p.age}{p.sex ? ` ${p.sex[0].toUpperCase()}` : ''}</span>}
                        {p.mrn && <span className="apcm-mrn"> · MRN {p.mrn}</span>}
                      </div>
                      <div className="ri-meta apcm-meta">
                        <span className="badge">{p.chronic} chronic dx</span>
                        <span className="badge">{programLabel(p.program)}</span>
                        <span className="badge">{mins}/{target} min · {shortBy}m short</span>
                        {submitted && <span className="badge">Submitted</span>}
                        {autoPolicy.enabled && (
                          <span className="badge apcm-auto-badge" title="Future short months can be decided automatically when all billing safeguards pass">
                            Auto fallback on
                          </span>
                        )}
                        {renderInsuranceBadges(p.insurances)}
                      </div>
                      <div className="ri-meta apcm-suggest">
                        Suggested: <b>{sug.label}</b> — <span style={{ color: 'var(--ink-2)' }}>{sug.reason}</span>
                      </div>
                      <div className="apcm-pills">
                        <QmbPill patient={p} onSaved={(next) => patchLocalPatient(p.id, next)} />
                        {renderApcmConsentStatus(p)}
                        <label
                          className={`apcm-auto-toggle ${autoPolicy.enabled ? 'on' : ''}`}
                          title="When this patient misses the CCM threshold in a future month, automatically record the APCM fallback decision once consent and billing safeguards pass"
                        >
                          <input
                            type="checkbox"
                            checked={autoPolicy.enabled}
                            disabled={busy[p.id]}
                            onChange={(e) => toggleAutoFallback(p, e.target.checked)}
                          />
                          <span>Auto future fallback</span>
                        </label>
                        <BhiG0570Chip
                          status={bhiG0570StatusForRow(p, month, apcmContextActive)}
                          apcmContextActive={apcmContextActive}
                        />
                        <button
                          className="btn ghost sm"
                          onClick={() => setEligibilityExpanded(prev => ({ ...prev, [p.id]: !prev[p.id] }))}>
                          {eligibilityExpanded[p.id] ? 'Hide eligibility' : 'Eligibility'}
                        </button>
                      </div>
                      {decided && (
                        <div className="apcm-decided-stamp">
                          Decided · <b>{decided.code}</b>{decided.source === 'auto' ? ' · Auto' : ''} · {decided.ts?.slice(0, 10) || ''}
                        </div>
                      )}
                      {submittedOnly && (
                        <div className="apcm-decided-stamp">
                          Submitted for {month}; reverse from Studio regenerate before changing APCM fallback.
                        </div>
                      )}
                    </div>
                    <div className="apcm-row-actions">
                      {decided ? (
                        <>
                          <button
                            className="btn ghost sm"
                            disabled={busy[p.id] || submitted || billedStateUnavailable}
                            title={submitted
                              ? 'Already submitted. Regenerate in Studio first to clear submitted status.'
                              : billedStateUnavailable
                                ? 'Billed state unavailable'
                                : 'Reverse APCM fallback'}
                            onClick={() => reverseRow(p)}>
                            Reverse
                          </button>
                        </>
                      ) : (
                        <button className="btn sm" disabled title="Already submitted">
                          Submitted
                        </button>
                      )}
                    </div>
                  </div>
                  {eligibilityExpanded[p.id] ? (
                    <div className="row-eligibility-drawer">
                      <EligibilityPanel patient={p} onRefreshed={(payload) => patchFromEligibility(p.id, payload)} />
                    </div>
                  ) : null}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {showBelowChronic && belowChronic.length > 0 && (
        <div className="apcm-below-section">
          <div className="apcm-section-divider">
            <span>Below CCM threshold · {belowChronic.length} patient{belowChronic.length !== 1 ? 's' : ''} · &lt;2 chronic conditions</span>
          </div>
          <div className="apcm-list">
            {belowChronic.map(p => {
              const target = targetFor(p);
              const mins = ccmMonthlyMinutes(p.raw, month);
              const shortBy = Math.max(0, target - mins);
              const sug = qmbSafeApcmSuggestion(p);
              const apcmMap = getApcmMap(p.raw);
              const decided = month ? apcmMap[month] : null;
              const autoPolicy = getApcmAutoFallbackPolicy(p.raw);
              const blockers = getDecisionBlockers(p, sug, { captureConsent: true });
              const acceptDisabled = blockers.length > 0 || busy[p.id];
              const acceptTitle = blockers[0] || `Accept ${sug.code} for ${month}`;
              return (
                <React.Fragment key={p.id}>
                <div
                  ref={(el) => { rowRefs.current[p.id] = el; }}
                  className={`roster-item apcm-row ${decided ? 'apcm-decided' : ''}`}
                >
                  <div className={`avatar av-${p.avSlot}`}>{nameInitials(p.raw)}</div>
                  <div className="apcm-row-main">
                    <div className="ri-name">
                      {p.name}
                      {p.age != null && <span className="apcm-age"> · {p.age}{p.sex ? ` ${p.sex[0].toUpperCase()}` : ''}</span>}
                      {p.mrn && <span className="apcm-mrn"> · MRN {p.mrn}</span>}
                    </div>
                    <div className="ri-meta apcm-meta">
                      <span className="badge warn">{p.chronic} chronic dx</span>
                      <span className="badge">{programLabel(p.program)}</span>
                      <span className="badge">{mins}/{target} min · {shortBy}m short</span>
                      {autoPolicy.enabled && (
                        <span className="badge apcm-auto-badge" title="Future G0556 fallback can be decided automatically when all billing safeguards pass">
                          Auto G0556 on
                        </span>
                      )}
                      {renderInsuranceBadges(p.insurances)}
                    </div>
                    <div className="ri-meta apcm-suggest">
                      Suggested: <b>{sug.label}</b> — <span style={{ color: 'var(--ink-2)' }}>{sug.reason}</span>
                    </div>
                    <div className="apcm-pills">
                      <QmbPill patient={p} onSaved={(next) => patchLocalPatient(p.id, next)} />
                      {renderApcmConsentStatus(p)}
                      <label
                        className={`apcm-auto-toggle ${autoPolicy.enabled ? 'on' : ''}`}
                        title="Explicitly opt this 0/1 chronic patient into automatic G0556 fallback for future short months"
                      >
                        <input
                          type="checkbox"
                          checked={autoPolicy.enabled}
                          disabled={busy[p.id]}
                          onChange={(e) => toggleAutoFallback(p, e.target.checked)}
                        />
                        <span>Auto future G0556</span>
                      </label>
                      <BhiG0570Chip
                        status={bhiG0570StatusForRow(p, month, true)}
                        apcmContextActive={true}
                      />
                      <button
                        className="btn ghost sm"
                        onClick={() => setEligibilityExpanded(prev => ({ ...prev, [p.id]: !prev[p.id] }))}>
                        {eligibilityExpanded[p.id] ? 'Hide eligibility' : 'Eligibility'}
                      </button>
                    </div>
                    {decided && (
                      <div className="apcm-decided-stamp">
                        Decided · <b>{decided.code}</b>{decided.source === 'auto' ? ' · Auto' : ''} · {decided.ts?.slice(0, 10) || ''}
                      </div>
                    )}
                  </div>
                  <div className="apcm-row-actions">
                    {decided ? (
                      <>
                        <button className="btn ghost sm" disabled={busy[p.id]} title="Reverse APCM fallback before choosing another tier" onClick={() => reverseRow(p)}>Reverse</button>
                      </>
                    ) : (
                      <button
                        className="btn primary sm"
                        disabled={acceptDisabled}
                        title={acceptTitle}
                        onClick={() => acceptRow(p, sug)}>
                        {busy[p.id] ? '…' : `Accept · ${sug.code}`}
                      </button>
                    )}
                  </div>
                </div>
                {eligibilityExpanded[p.id] ? (
                  <div className="row-eligibility-drawer">
                    <EligibilityPanel patient={p} onRefreshed={(payload) => patchFromEligibility(p.id, payload)} />
                  </div>
                ) : null}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      {toast && (
        <div className={`apcm-toast ${toast.error ? 'error' : ''}`}>{toast.text}</div>
      )}
    </section>
  );
}
